import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { supabase } from '../../lib/supabase';
import AdminLayout from '../../components/admin/AdminLayout';
import { toast } from 'react-toastify';
import { Save, User, Lock, Check, Info, Phone, BatteryWarning as BrandingWatermark } from 'lucide-react';

const Settings = () => {
  const [changePassword, setChangePassword] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      phone: '',
      whatsapp: '',
      google_analytics_id: '',
      google_ads_id: '',
      google_ads_conversion_id: '',
      facebook_pixel_id: '',
      tiktok_pixel_id: '',
      gtm_id: ''
    }
  });

  const newPassword = watch('newPassword');

  useEffect(() => {
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        setValue('email', user.email || '');
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const onSubmit = async (formData: any) => {
    setSubmitLoading(true);
    
    try {
      // Handle password change if requested
      if (changePassword) {
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error('Le password non coincidono');
          return;
        }
        
        const { error } = await supabase.auth.updateUser({
          password: formData.newPassword
        });
        
        if (error) throw error;
        
        toast.success('Password aggiornata con successo');
      }
      
      setSubmitLoading(false);
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast.error(`Errore: ${error.message || 'Si è verificato un errore durante l\'aggiornamento'}`);
      setSubmitLoading(false);
    }
  };

  return (
    <AdminLayout title="Impostazioni">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b flex items-center">
                <Phone className="h-5 w-5 mr-2 text-primary" />
                Contatti
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numero di Telefono
                  </label>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="tel"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        placeholder="Es. +39 327 8855115"
                      />
                    )}
                  />
                  <p className="text-xs text-gray-500 mt-1">Numero visualizzato nelle CTA di chiamata</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Numero WhatsApp
                  </label>
                  <Controller
                    name="whatsapp"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="tel"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        placeholder="Es. +39 327 8855115"
                      />
                    )}
                  />
                  <p className="text-xs text-gray-500 mt-1">Numero per le chat WhatsApp</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b flex items-center">
                <BrandingWatermark className="h-5 w-5 mr-2 text-primary" />
                Tracking e Analytics
              </h3>
              
              <div className="space-y-6">
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Google Analytics</h4>
                  <Controller
                    name="google_analytics_id"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        placeholder="Es. G-XXXXXXXXXX"
                      />
                    )}
                  />
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Google Ads</h4>
                  <div className="space-y-3">
                    <Controller
                      name="google_ads_id"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                          placeholder="ID Google Ads"
                        />
                      )}
                    />
                    <Controller
                      name="google_ads_conversion_id"
                      control={control}
                      render={({ field }) => (
                        <input
                          {...field}
                          type="text"
                          className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                          placeholder="ID Conversione"
                        />
                      )}
                    />
                  </div>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Facebook Pixel</h4>
                  <Controller
                    name="facebook_pixel_id"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        placeholder="ID Facebook Pixel"
                      />
                    )}
                  />
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">TikTok Pixel</h4>
                  <Controller
                    name="tiktok_pixel_id"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        placeholder="ID TikTok Pixel"
                      />
                    )}
                  />
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-800 mb-3">Google Tag Manager</h4>
                  <Controller
                    name="gtm_id"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary focus:border-primary"
                        placeholder="Es. GTM-XXXXXX"
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold mb-4 pb-2 border-b flex items-center">
                <User className="h-5 w-5 mr-2 text-primary" />
                Impostazioni Account
              </h3>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="email"
                      readOnly
                      disabled
                      className="w-full border border-gray-300 bg-gray-50 rounded-md px-3 py-2"
                    />
                  )}
                />
                <p className="text-xs text-gray-500 mt-1">L'email non può essere modificata in questa interfaccia</p>
              </div>
              
              <div>
                <div className="flex items-center mb-4">
                  <h4 className="font-medium text-gray-800 flex items-center">
                    <Lock className="h-5 w-5 mr-2 text-primary" />
                    Modifica Password
                  </h4>
                  <div className="ml-auto">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={changePassword}
                        onChange={() => setChangePassword(!changePassword)}
                        className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <span className="ml-2 text-sm text-gray-600">Voglio modificare la password</span>
                    </label>
                  </div>
                </div>
                
                {changePassword && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nuova Password*
                      </label>
                      <Controller
                        name="newPassword"
                        control={control}
                        rules={{ 
                          required: 'La nuova password è obbligatoria',
                          minLength: {
                            value: 6,
                            message: 'La password deve contenere almeno 6 caratteri'
                          }
                        }}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="password"
                            className={`w-full border ${errors.newPassword ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                          />
                        )}
                      />
                      {errors.newPassword && (
                        <p className="text-red-500 text-sm mt-1">{errors.newPassword.message}</p>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Conferma Password*
                      </label>
                      <Controller
                        name="confirmPassword"
                        control={control}
                        rules={{ 
                          required: 'La conferma della password è obbligatoria',
                          validate: value => 
                            value === newPassword || 'Le password non coincidono'
                        }}
                        render={({ field }) => (
                          <input
                            {...field}
                            type="password"
                            className={`w-full border ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'} rounded-md px-3 py-2 focus:ring-primary focus:border-primary`}
                          />
                        )}
                      />
                      {errors.confirmPassword && (
                        <p className="text-red-500 text-sm mt-1">{errors.confirmPassword.message}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Form Actions */}
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={submitLoading || (!changePassword)}
                className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-light transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitLoading ? (
                  <>
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Aggiornamento...
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5 mr-2" />
                    Salva Modifiche
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
        
        <div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h3 className="text-lg font-semibold mb-4 pb-2 border-b flex items-center">
              <Info className="h-5 w-5 mr-2 text-primary" />
              Informazioni
            </h3>
            
            <div className="space-y-4">
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Versione</h4>
                <p className="text-gray-600">1.0.0</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Sviluppato da</h4>
                <p className="text-gray-600">Molinaro Immobiliare</p>
              </div>
              
              <div>
                <h4 className="font-medium text-gray-700 mb-1">Contatti Supporto</h4>
                <p className="text-gray-600">supporto@molinaroimmobiliare.it</p>
              </div>
            </div>
            
            <div className="mt-8 bg-primary/5 p-4 rounded-lg">
              <div className="flex items-start">
                <Check className="h-5 w-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-700 mb-1">Gestione Database</h4>
                  <p className="text-gray-600 text-sm">
                    Il database è gestito tramite Supabase. Per operazioni avanzate, contattare l'amministratore di sistema.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Settings;